import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { staffApi } from '../staff';

export const staffKeys = {
  teachingOptions: ['staff', 'teaching-options'] as const,
  detail: (userId: string) => ['staff', userId, 'user'] as const,
  profile: (userId: string) => ['staff', userId, 'profile'] as const,
};

export function useStaffDetailQuery(userId: string) {
  return useQuery({ queryKey: staffKeys.detail(userId), queryFn: () => staffApi.getUser(userId), enabled: !!userId });
}

export function useStaffProfileQuery(userId: string) {
  return useQuery({
    queryKey: staffKeys.profile(userId),
    queryFn: () => staffApi.getProfile(userId),
    enabled: !!userId,
  });
}

/** Staff users eligible to be assigned as a timetable teacher (lead, assistant, or homeroom). */
export function useTeachingOptionsQuery() {
  return useQuery({
    queryKey: staffKeys.teachingOptions,
    queryFn: () => staffApi.teachingOptions(),
  });
}

/** Sets (or clears, with `roomId: null`) a staff user's default room on the Teacher-rooms setup screen. */
export function useSetDefaultRoomMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, roomId }: { id: string; roomId: string | null }) => staffApi.setDefaultRoom(id, roomId),
    onSuccess: () => qc.invalidateQueries({ queryKey: staffKeys.teachingOptions }),
  });
}

export function useStaffOptionsQuery(enabled = true, options: { includeEmployees?: boolean } = {}) {
  const includeEmployees = options.includeEmployees ?? false;
  return useQuery({
    queryKey: ['staff', 'options', includeEmployees ? 'with-employees' : 'teaching'],
    queryFn: () => staffApi.listOptions(includeEmployees),
    enabled,
  });
}
